import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { UserPlus, Users, CheckCircle, X, Building2, BarChart3, RefreshCw } from "lucide-react";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { fetchAvailableUsers, fetchUsers } from "../../services/crmServices/userService";
import { fetchAvailableDesks } from "../../services/crmServices/deskService";
import { bulkAssignLeads, bulkUpdateLeads, fetchStatusBreakdown } from "../../services/crmServices/leadService";
import { useAuth } from "../../hooks/useAuth";

// Make sure STORAGE_KEYS is defined at the top
const STORAGE_KEYS = {
    AGENTS: 'bulkAssignModal_agents',
    DESKS: 'bulkAssignModal_desks'
};


export function BulkAssignModal({ open, onClose, selectedLeads, onSuccess }) {
    const { user } = useAuth();
    
    // Define keys inside component to ensure they're accessible
    const AGENTS_KEY = 'bulkAssignModal_agents';
    const DESKS_KEY = 'bulkAssignModal_desks';
    
    // Helper function to load from localStorage
    const loadFromLocalStorage = (key, defaultValue = []) => {
        try {
            const cached = localStorage.getItem(key);
            if (cached) {
                return JSON.parse(cached);
            }
        } catch (error) {
            console.error(`Error loading ${key} from localStorage:`, error);
        }
        return defaultValue;
    };
    
    // Initialize state directly from localStorage for instant load
    const cachedAgents = loadFromLocalStorage(AGENTS_KEY, []);
    const cachedDesks = loadFromLocalStorage(DESKS_KEY, []);
    const cachedFloorManagers = cachedAgents.filter((user) => user.position?.role === "floorManager");
    const cachedGeneralManagers = cachedAgents.filter((user) => user.position?.role === "generalManager");
    
    const [agents, setAgents] = useState(cachedAgents);
    const [allAgents, setAllAgents] = useState(cachedAgents);
    const [floorManagers, setFloorManagers] = useState(cachedFloorManagers);
    const [generalManagers, setGeneralManagers] = useState(cachedGeneralManagers);
    const [desks, setDesks] = useState(cachedDesks);
    
    const [selectedAgent, setSelectedAgent] = useState("");
    const [selectedDesk, setSelectedDesk] = useState("");
    const [selectedFloorManager, setSelectedFloorManager] = useState(""); // For Floor Manager assignment
    const [selectedGeneralManager, setSelectedGeneralManager] = useState(""); // For General Manager assignment
    const [selectedAgentDesk, setSelectedAgentDesk] = useState(""); // For filtering agents by desk
    const [assignmentType, setAssignmentType] = useState("agent"); // "agent", "desk", "floorManager", or "generalManager"
    const [loading, setLoading] = useState(false);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [floorManagersLoading, setFloorManagersLoading] = useState(false);
    const [generalManagersLoading, setGeneralManagersLoading] = useState(false);
    const [desksLoading, setDesksLoading] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
    const [statusBreakdown, setStatusBreakdown] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);

    const role = user?.position?.role;
    const canAssignToDesk = role === "admin" || role === "floorManager";
    const canAssignToFloorManager = role === "admin"; // Only admin can assign to Floor Manager
    const canAssignToGeneralManager = role === "admin"; // Only admin can assign to General Manager

    // Load from localStorage and fetch fresh data when modal opens
    useEffect(() => {
        if (open) {
            // Load from localStorage first (instant display)
            const cachedAgentsData = loadFromLocalStorage(AGENTS_KEY, []);
            const cachedDesksData = loadFromLocalStorage(DESKS_KEY, []);
            
            if (cachedAgentsData.length > 0) {
                setAllAgents(cachedAgentsData);
                setAgents(cachedAgentsData);
                const floorManagerUsers = cachedAgentsData.filter((user) => user.position?.role === "floorManager");
                const generalManagerUsers = cachedAgentsData.filter((user) => user.position?.role === "generalManager");
                setFloorManagers(floorManagerUsers);
                setGeneralManagers(generalManagerUsers);
            }
            
            if (cachedDesksData.length > 0) {
                setDesks(cachedDesksData);
            }
            
            // Fetch fresh data in background (only show loading if no cached data)
            if (cachedAgentsData.length === 0) {
                setAgentsLoading(true);
            }
            fetchAgents();
            
            if (cachedDesksData.length === 0) {
                setDesksLoading(true);
            }
            fetchDesks();
            
            if (canAssignToFloorManager) {
                if (cachedAgentsData.length > 0) {
                    // Floor managers already loaded from cached agents
                    const floorManagerUsers = cachedAgentsData.filter((user) => user.position?.role === "floorManager");
                    setFloorManagers(floorManagerUsers);
                } else {
                    fetchFloorManagers();
                }
            }
            
            if (canAssignToGeneralManager) {
                if (cachedAgentsData.length > 0) {
                    // General managers already loaded from cached agents
                    const generalManagerUsers = cachedAgentsData.filter((user) => user.position?.role === "generalManager");
                    setGeneralManagers(generalManagerUsers);
                }
            }
        }
    }, [open, canAssignToFloorManager, canAssignToGeneralManager]);

    const fetchAgents = async () => {
        // Check if we have cached data to determine if we should show loading
        const hasCached = loadFromLocalStorage(AGENTS_KEY, []).length > 0;
        
        if (!hasCached) {
            setAgentsLoading(true);
        }
        
        try {
            const response = await fetchAvailableUsers();
            
            // Check different possible response structures
            let allUsers = null;
            if (Array.isArray(response)) {
                allUsers = response;
            } else if (response?.data) {
                allUsers = Array.isArray(response.data) ? response.data : response.data;
            } else if (response?.users) {
                allUsers = response.users;
            }
            
            if (allUsers && Array.isArray(allUsers)) {
                setAllAgents(allUsers);
                setAgents(allUsers);
                
                // Update floor managers and general managers from fresh data
                const floorManagerUsers = allUsers.filter((user) => user.position?.role === "floorManager");
                const generalManagerUsers = allUsers.filter((user) => user.position?.role === "generalManager");
                setFloorManagers(floorManagerUsers);
                setGeneralManagers(generalManagerUsers);
                
                // Save to localStorage
                try {
                    // Clean the data to remove any potential circular references
                    const cleanUsers = allUsers.map(user => {
                        return {
                            _id: user._id,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            email: user.email,
                            position: user.position ? {
                                _id: user.position._id,
                                name: user.position.name,
                                role: user.position.role
                            } : null,
                            agentDesk: user.agentDesk ? {
                                _id: user.agentDesk._id,
                                name: user.agentDesk.name
                            } : null
                        };
                    });
                    
                    const jsonString = JSON.stringify(cleanUsers);
                    localStorage.setItem(AGENTS_KEY, jsonString);
                } catch (storageError) {
                    console.error("localStorage error for agents:", storageError);
                    if (storageError.name === 'QuotaExceededError') {
                        console.error("localStorage quota exceeded for agents!");
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            if (!hasCached) {
                toast.error("Failed to load users");
            }
        } finally {
            if (!hasCached) {
                setAgentsLoading(false);
            }
        }
    };

    const fetchDesks = async () => {
        // Check if we have cached data
        const hasCached = loadFromLocalStorage(DESKS_KEY, []).length > 0;
        
        if (!hasCached) {
            setDesksLoading(true);
        }
        
        try {
            const response = await fetchAvailableDesks();
            
            // Check different possible response structures
            let desksData = null;
            if (Array.isArray(response)) {
                desksData = response;
            } else if (response?.data) {
                desksData = Array.isArray(response.data) ? response.data : response.data;
            }
            
            if (desksData && Array.isArray(desksData) && desksData.length > 0) {
                setDesks(desksData);
                
                // Save to localStorage
                try {
                    const jsonString = JSON.stringify(desksData);
                    localStorage.setItem(DESKS_KEY, jsonString);
                } catch (storageError) {
                    console.error("localStorage error for desks:", storageError);
                }
            }
        } catch (error) {
            console.error("Error fetching desks:", error);
            if (!hasCached) {
                toast.error("Failed to load desks");
            }
        } finally {
            if (!hasCached) {
                setDesksLoading(false);
            }
        }
    };

    const fetchFloorManagers = async () => {
        setFloorManagersLoading(true);
        try {
            const response = await fetchAvailableUsers();
            if (response?.data) {
                // Filter for floor managers only
                const floorManagerUsers = response.data.filter((user) => user.position?.role === "floorManager");
                setFloorManagers(floorManagerUsers);
            }
        } catch (error) {
            console.error("Error fetching floor managers:", error);
            toast.error("Failed to load floor managers");
        } finally {
            setFloorManagersLoading(false);
        }
    };

    // Filter users based on selected desk
    const handleAgentDeskChange = (deskId) => {
        setSelectedAgentDesk(deskId);
        setSelectedAgent(""); // Reset selected user when desk changes

        if (deskId) {
            // Filter users by selected desk
            const filteredUsers = allAgents.filter((agent) => agent.agentDesk?._id === deskId);
            setAgents(filteredUsers);
        } else {
            // Show all users if no desk is selected
            setAgents(allAgents);
        }
    };

    const handleAssign = async () => {
        if (assignmentType === "agent" && !selectedAgent) {
            toast.error("Please select a user");
            return;
        }

        if (assignmentType === "desk" && !selectedDesk) {
            toast.error("Please select a desk");
            return;
        }

        if (assignmentType === "floorManager" && !selectedFloorManager) {
            toast.error("Please select a floor manager");
            return;
        }

        if (assignmentType === "generalManager" && !selectedGeneralManager) {
            toast.error("Please select a general manager");
            return;
        }

        setLoading(true);
        const startTime = Date.now();
        const totalLeads = selectedLeads.length;
        setProgress({ current: 0, total: totalLeads, percentage: 0 });

        const targetText = assignmentType === "agent" 
            ? "user" 
            : assignmentType === "floorManager"
            ? "floor manager"
            : assignmentType === "generalManager"
            ? "general manager"
            : "desk";

        // Show progress toast
        const progressToast = toast.loading(`Preparing to assign ${totalLeads.toLocaleString()} leads to ${targetText}...`);

        try {
            const leadIds = selectedLeads.map((lead) => lead._id);

            let assignmentData;
            if (assignmentType === "agent") {
                assignmentData = {
                    leadIds,
                    updates: {
                        agent: selectedAgent,
                        desk: allAgents.find((agent) => agent._id === selectedAgent)?.agentDesk?._id || null
                    }
                };
            } else if (assignmentType === "floorManager") {
                assignmentData = {
                    leadIds,
                    updates: {
                        floorManager: selectedFloorManager,
                        desk: null,
                        agent: null
                    }
                };
            } else if (assignmentType === "generalManager") {
                assignmentData = {
                    leadIds,
                    updates: {
                        generalManager: selectedGeneralManager,
                        desk: null,
                        agent: null,
                        floorManager: null
                    }
                };
            } else {
                assignmentData = {
                    leadIds,
                    updates: {
                        desk: selectedDesk,
                        agent: null
                    }
                };
            }

            // Enhanced progress simulation
            let progressStep = 0;
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    progressStep++;
                    const progressValue = Math.min(95, Math.round(100 * (1 - Math.exp(-progressStep / 8))));
                    const currentCount = Math.round((progressValue / 100) * totalLeads);
                    const elapsed = Math.round((Date.now() - startTime) / 1000);
                    const estimated = progressValue > 5 ? Math.round((elapsed / progressValue) * 100) : 0;
                    const remaining = Math.max(0, estimated - elapsed);
                    
                    toast.loading(
                        `Assigning ${currentCount.toLocaleString()} / ${totalLeads.toLocaleString()} leads (${progressValue}%) • ${remaining}s remaining`,
                        { id: progressToast }
                    );
                    return { current: currentCount, total: totalLeads, percentage: progressValue };
                });
            }, 300);

            const response = await bulkUpdateLeads(assignmentData);

            clearInterval(progressInterval);
            const elapsed = Math.round((Date.now() - startTime) / 1000);
            toast.dismiss(progressToast);
            setProgress({ current: totalLeads, total: totalLeads, percentage: 100 });

            if (response?.status === 200) {
                const actualCount = response?.data?.count || selectedLeads.length;
                const timeText = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
                const successMessage = `✓ Successfully assigned ${actualCount.toLocaleString()} leads to ${targetText} in ${timeText}`;
                toast.success(successMessage, { duration: 5000 });
                onSuccess();
                handleClose();
            } else {
                const errorMessage =
                    assignmentType === "agent"
                        ? "Failed to assign leads to user"
                        : assignmentType === "floorManager"
                        ? "Failed to assign leads to floor manager"
                        : assignmentType === "generalManager"
                        ? "Failed to assign leads to general manager"
                        : "Failed to assign leads to desk";
                toast.error(errorMessage);
            }
        } catch (error) {
            toast.dismiss(progressToast);
            console.error("Error assigning leads:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to assign leads";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleClose = () => {
        setSelectedAgent("");
        setSelectedDesk("");
        setSelectedFloorManager("");
        setSelectedGeneralManager("");
        setSelectedAgentDesk("");
        setAssignmentType("agent");
        setStatusBreakdown(null);
        setAgents(allAgents);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle className="flex items-center">
                        <UserPlus className="h-5 w-5 mr-2" />
                        Bulk Assign Leads
                    </DialogTitle>
                    <DialogDescription>
                        Assign {selectedLeads.length} selected leads to{" "}
                        {assignmentType === "agent"
                            ? "a user"
                            : assignmentType === "floorManager"
                            ? "a floor manager"
                            : assignmentType === "generalManager"
                            ? "a general manager"
                            : "a desk"}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 overflow-y-auto flex-1 pr-2 -mr-2">
                    {/* Status Breakdown of Selected Leads */}
                    {selectedLeads.length > 0 && (
                        <Card className="border-indigo-100 bg-indigo-50/40">
                            <CardHeader className="pb-2 pt-3 px-4">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                        <BarChart3 className="h-4 w-4 text-indigo-500" />
                                        Status Breakdown
                                        <span className="text-xs font-normal text-gray-500 ml-1">({selectedLeads.length} leads)</span>
                                    </CardTitle>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={statusLoading}
                                        onClick={async () => {
                                            setStatusLoading(true);
                                            try {
                                                const leadIds = selectedLeads.map(l => l._id);
                                                const response = await fetchStatusBreakdown(leadIds);
                                                if (response?.data) {
                                                    setStatusBreakdown(response.data);
                                                }
                                            } catch (error) {
                                                console.error("Error fetching status breakdown:", error);
                                                toast.error("Failed to load status breakdown");
                                            } finally {
                                                setStatusLoading(false);
                                            }
                                        }}
                                        className="h-7 text-xs"
                                    >
                                        {statusLoading ? (
                                            <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                                        ) : (
                                            <BarChart3 className="h-3 w-3 mr-1" />
                                        )}
                                        {statusBreakdown ? "Refresh" : "Get Statuses"}
                                    </Button>
                                </div>
                            </CardHeader>
                            {statusBreakdown && (
                                <CardContent className="pb-3 px-4 pt-0">
                                    <div className="flex flex-wrap gap-1.5">
                                        {statusBreakdown.map((item) => (
                                            <Badge
                                                key={item.status}
                                                variant="secondary"
                                                className="text-xs px-2 py-1 bg-white border border-gray-200 text-gray-700"
                                            >
                                                {item.status}: <span className="font-semibold ml-0.5">{item.count}</span>
                                            </Badge>
                                        ))}
                                    </div>
                                </CardContent>
                            )}
                        </Card>
                    )}

                    {/* Assignment Type Selection */}
                    {(canAssignToDesk || canAssignToFloorManager || canAssignToGeneralManager) && (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">Assignment Type</CardTitle>
                                <CardDescription>Choose how to assign the selected leads</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <RadioGroup
                                    value={assignmentType}
                                    onValueChange={(value) => {
                                        setAssignmentType(value);
                                        // Reset selections when changing assignment type
                                        setSelectedAgent("");
                                        setSelectedDesk("");
                                        setSelectedFloorManager("");
                                        setSelectedGeneralManager("");
                                    }}
                                    className="flex flex-col space-y-3"
                                >
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="agent" id="agent" />
                                        <Label htmlFor="agent" className="text-sm font-normal">
                                            <div className="flex items-center">
                                                <UserPlus className="h-4 w-4 mr-2" />
                                                Assign to User
                                            </div>
                                        </Label>
                                    </div>
                                    {canAssignToDesk && (
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="desk" id="desk" />
                                            <Label htmlFor="desk" className="text-sm font-normal">
                                                <div className="flex items-center">
                                                    <Building2 className="h-4 w-4 mr-2" />
                                                    Assign to Desk
                                                </div>
                                            </Label>
                                        </div>
                                    )}
                                    {canAssignToFloorManager && (
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="floorManager" id="floorManager" />
                                            <Label htmlFor="floorManager" className="text-sm font-normal">
                                                <div className="flex items-center">
                                                    <Users className="h-4 w-4 mr-2" />
                                                    Assign to Floor Manager
                                                </div>
                                            </Label>
                                        </div>
                                    )}
                                    {canAssignToGeneralManager && (
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="generalManager" id="generalManager" />
                                            <Label htmlFor="generalManager" className="text-sm font-normal">
                                                <div className="flex items-center">
                                                    <Users className="h-4 w-4 mr-2" />
                                                    Assign to General Manager
                                                </div>
                                            </Label>
                                        </div>
                                    )}
                                </RadioGroup>
                            </CardContent>
                        </Card>
                    )}

                    {/* Agent Selection */}
                    {assignmentType === "agent" && (
                        <div className="space-y-4">
                            {/* Desk Filter for Users */}
                            <div className="space-y-3">
                                <Label htmlFor="agent-desk-filter">Filter by Desk (Optional)</Label>
                                {desksLoading ? (
                                    <div className="flex items-center justify-center py-4">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                        <span className="ml-2 text-sm text-gray-500">Loading desks...</span>
                                    </div>
                                ) : (
                                    <Select
                                        value={selectedAgentDesk}
                                        onChange={handleAgentDeskChange}
                                        options={[
                                            { value: "", label: "All Desks" },
                                            ...desks.map((desk) => ({
                                                value: desk._id,
                                                label: desk.name
                                            }))
                                        ]}
                                        placeholder="Select a desk to filter users"
                                    />
                                )}
                            </div>

                            {/* Agent Selection */}
                            <div className="space-y-3">
                                <Label htmlFor="agent-select">Select User *</Label>
                                {agentsLoading ? (
                                    <div className="flex items-center justify-center py-4">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                        <span className="ml-2 text-sm text-gray-500">Loading users...</span>
                                    </div>
                                ) : (
                                    <Select
                                        value={selectedAgent}
                                        onChange={setSelectedAgent}
                                        options={agents.map((agent) => ({
                                            value: agent._id,
                                            label: `${agent.firstName} ${agent.lastName} - ${
                                                agent.position?.name || agent.position?.role
                                            } ${agent.agentDesk?.name ? `(${agent.agentDesk.name})` : ""}`
                                        }))}
                                        placeholder={
                                            agents.length === 0 && selectedAgentDesk
                                                ? "No users found in selected desk"
                                                : "Choose a user to assign leads to"
                                        }
                                        searchable={true}
                                        searchPlaceholder="Search users by name, role, or desk..."
                                        disabled={agents.length === 0}
                                    />
                                )}
                                {selectedAgentDesk && agents.length === 0 && (
                                    <p className="text-sm text-gray-500 mt-1">
                                        No users found in the selected desk. Try selecting a different desk or
                                        &quot;All Desks&quot;.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Floor Manager Selection */}
                    {assignmentType === "floorManager" && canAssignToFloorManager && (
                        <div className="space-y-3">
                            <Label htmlFor="floorManager-select">Select Floor Manager *</Label>
                            {floorManagersLoading ? (
                                <div className="flex items-center justify-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                    <span className="ml-2 text-sm text-gray-500">Loading floor managers...</span>
                                </div>
                            ) : (
                                <Select
                                    value={selectedFloorManager}
                                    onChange={setSelectedFloorManager}
                                    options={floorManagers.map((floorManager) => ({
                                        value: floorManager._id,
                                        label: `${floorManager.firstName} ${floorManager.lastName} - ${
                                            floorManager.position?.name || floorManager.position?.role
                                        }`
                                    }))}
                                    placeholder="Choose a floor manager to assign leads to"
                                    searchable={true}
                                    searchPlaceholder="Search floor managers by name..."
                                    disabled={floorManagers.length === 0}
                                />
                            )}
                            {floorManagers.length === 0 && !floorManagersLoading && (
                                <p className="text-sm text-gray-500 mt-1">
                                    No floor managers found. Please create a floor manager first.
                                </p>
                            )}
                        </div>
                    )}

                    {/* General Manager Selection */}
                    {assignmentType === "generalManager" && canAssignToGeneralManager && (
                        <div className="space-y-3">
                            <Label htmlFor="generalManager-select">Select General Manager *</Label>
                            {generalManagersLoading ? (
                                <div className="flex items-center justify-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                    <span className="ml-2 text-sm text-gray-500">Loading general managers...</span>
                                </div>
                            ) : (
                                <Select
                                    value={selectedGeneralManager}
                                    onChange={setSelectedGeneralManager}
                                    options={generalManagers.map((generalManager) => ({
                                        value: generalManager._id,
                                        label: `${generalManager.firstName} ${generalManager.lastName} - ${
                                            generalManager.position?.name || generalManager.position?.role
                                        }`
                                    }))}
                                    placeholder="Choose a general manager to assign leads to"
                                    searchable={true}
                                    searchPlaceholder="Search general managers by name..."
                                    disabled={generalManagers.length === 0}
                                />
                            )}
                            {generalManagers.length === 0 && !generalManagersLoading && (
                                <p className="text-sm text-gray-500 mt-1">
                                    No general managers found. Please create a general manager first.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Desk Selection */}
                    {assignmentType === "desk" && canAssignToDesk && (
                        <div className="space-y-3">
                            <Label htmlFor="desk-select">Select Desk *</Label>
                            {desksLoading ? (
                                <div className="flex items-center justify-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                    <span className="ml-2 text-sm text-gray-500">Loading desks...</span>
                                </div>
                            ) : (
                                <Select
                                    value={selectedDesk}
                                    onChange={setSelectedDesk}
                                    options={desks.map((desk) => ({
                                        value: desk._id,
                                        label: desk.name
                                    }))}
                                    placeholder="Choose a desk to assign leads to"
                                />
                            )}
                        </div>
                    )}

                    {/* Progress Bar */}
                    {loading && progress.total > 0 && (
                        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 shadow-lg">
                            <CardContent className="pt-4">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-3">
                                            <div className="relative">
                                                <UserPlus className="h-5 w-5 text-indigo-600 animate-spin" style={{ animationDuration: '2s' }} />
                                                <div className="absolute inset-0 bg-indigo-400 rounded-full animate-ping opacity-20"></div>
                                            </div>
                                            <div>
                                                <span className="text-sm font-semibold text-indigo-900 block">
                                                    Assigning leads in progress
                                                </span>
                                                <span className="text-xs text-indigo-600">
                                                    Please wait, processing {progress.total.toLocaleString()} leads...
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-lg font-bold text-indigo-700 block">
                                                {progress.percentage}%
                                            </span>
                                            <span className="text-xs text-indigo-600">
                                                {progress.current.toLocaleString()} / {progress.total.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-indigo-200 rounded-full h-3 shadow-inner overflow-hidden">
                                        <div
                                            className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                                            style={{ width: `${progress.percentage}%` }}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Warning Message */}
                    <Card className="border-yellow-200 bg-yellow-50">
                        <CardContent className="pt-4">
                            <div className="flex items-start space-x-2">
                                <CheckCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium">Assignment Details:</p>
                                    <ul className="mt-1 list-disc list-inside space-y-1">
                                        <li>All selected leads will be assigned to the chosen {assignmentType}</li>
                                        <li>
                                            Previously assigned leads will be reassigned to the new {assignmentType}
                                        </li>
                                        <li>This action cannot be undone</li>
                                    </ul>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                </div>

                {/* Action Buttons - Sticky Footer */}
                <div className="flex space-x-3 pt-4 border-t bg-white flex-shrink-0 -mx-6 -mb-6 px-6 pb-6">
                    <Button
                        onClick={handleAssign}
                        disabled={
                            loading ||
                            (assignmentType === "agent" && (!selectedAgent || agentsLoading)) ||
                            (assignmentType === "desk" && (!selectedDesk || desksLoading)) ||
                            (assignmentType === "floorManager" && (!selectedFloorManager || floorManagersLoading)) ||
                            (assignmentType === "generalManager" && (!selectedGeneralManager || generalManagersLoading))
                        }
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Assigning...
                            </>
                        ) : (
                            <>
                                {assignmentType === "agent" ? (
                                    <UserPlus className="h-4 w-4 mr-2" />
                                ) : assignmentType === "floorManager" ? (
                                    <Users className="h-4 w-4 mr-2" />
                                ) : assignmentType === "generalManager" ? (
                                    <Users className="h-4 w-4 mr-2" />
                                ) : (
                                    <Building2 className="h-4 w-4 mr-2" />
                                )}
                                Assign {selectedLeads.length} Leads to{" "}
                                {assignmentType === "agent"
                                    ? "User"
                                    : assignmentType === "floorManager"
                                    ? "Floor Manager"
                                    : assignmentType === "generalManager"
                                    ? "General Manager"
                                    : "Desk"}
                            </>
                        )}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                        className="flex-1"
                    >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
